// src/frontend/layout/MainLayout.jsx
import React, { useEffect, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { createDomainTheme, getDomainColors } from "../../../theme";
import { Box, CircularProgress, GlobalStyles } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import MapPageHeader from "./MapPageHeader";
import HomeStatsBar from "./HomeStatsBar";
import HomePopularTopics from "./HomePopularTopics";
import HriFooter from "./HriFooter";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function MainLayout() {
    const location = useLocation();
    const [domainConfig, setDomainConfig] = useState({});
    const [configLoaded, setConfigLoaded] = useState(false);
    const domainTheme = React.useMemo(() => createDomainTheme(domainConfig), [domainConfig]);
    const cssColors = React.useMemo(() => Object.fromEntries(
        Object.entries(getDomainColors(domainConfig)).map(([key, value]) => [`--domain-${key.replace('color_', '').replaceAll('_', '-')}`, value])
    ), [domainConfig]);
    const isCompanyDetail = /^\/companies\/[^/]+/.test(location.pathname);
    const isHomePage = location.pathname === "/";

    useEffect(() => {
        let active = true;

        axios
            .get(`${API_BASE}/api/domains/current`, {
                params: { url: window.location.origin },
                timeout: 10000,
            })
            .then((res) => {
                if (active) setDomainConfig(res.data || {});
            })
            .catch(() => {
                if (active) setDomainConfig({});
            })
            .finally(() => { if (active) setConfigLoaded(true); });

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const icon = document.querySelector('link[rel="icon"]');
        if (!icon || !domainConfig.favicon) return undefined;
        const previous = icon.getAttribute("href");
        icon.setAttribute("href", /^https?:\/\//i.test(domainConfig.favicon) ? domainConfig.favicon : `${API_BASE}${domainConfig.favicon}`);
        return () => { if (previous === null) icon.removeAttribute("href"); else icon.setAttribute("href", previous); };
    }, [domainConfig.favicon]);

    if (!configLoaded) return <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}><CircularProgress /></Box>;

    return (
        <ThemeProvider theme={domainTheme}>
        <GlobalStyles styles={{ ":root": cssColors, body: { backgroundColor: domainTheme.palette.background.default } }} />
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "background.default",
                color: "text.primary",
                display: "flex",
                flexDirection: "column",
                overflowX: "hidden",
            }}
        >
            <Box>
                <MapPageHeader domainConfig={domainConfig} showHero={!isCompanyDetail} />
            </Box>

            {isHomePage && <HomeStatsBar />}
            {isHomePage && <HomePopularTopics />}

            <Box
                sx={{
                    flex: 1,
                    maxWidth: 1540,
                    mx: "auto",
                    px: { xs: 2, md: 4 },
                    pt: 4,
                    pb: isHomePage ? 0 : 4,
                    width: "100%",
                }}
            >
                <Outlet />
            </Box>

            <HriFooter mt={isHomePage ? 0 : 4} domainConfig={domainConfig} />
        </Box>
        </ThemeProvider>
    );
}
